import { useCallback, useEffect, useMemo, useState } from "react";
import { useGetCheckInDetailsById } from "../../hooks/visitor/query";
import { useLocation, useNavigate } from "react-router-dom";
import {
  CHECK_IN_OUT_TYPE_KEYS,
  DOC_TYPE_ID,
  STATUS_IDS,
  USER_ROLES_IDS,
  VISITOR_VISIT_STATUSES,
} from "../../constants/common";
import useAuthStore from "../../store/Auth";
import { APP_ROUTES } from "../../routes";
import toast, { Toaster } from "react-hot-toast";
import Loader from "../../components/loader";
import { Button } from "../../components/button";
import {
  formattedDate,
  formattedDateTimeWithoutSec,
  mapToOptions,
} from "../../lib/functions";
import Section from "./Section";
import InfoGrid from "./InfoGrid";
import { cn } from "../../utils/cn";
import { useTranslation } from "react-i18next";
import { Controller } from "react-hook-form";
import CheckInVisitorModal from "../../components/modal/CheckInVisitorModal";
import CheckboxInput from "../../components/inputs/CheckboxInput";
import CheckOutVisitorModal from "../../components/modal/CheckOutVisitorModal";
import ComboboxInput from "../../components/inputs/ComboboxInput";
import useFormWithSchema from "../../hooks/useFormWithSchema";
import { useGetAllGatesById } from "../../hooks/common/query";
import * as Yup from "yup";

const CheckInSchema = Yup.object({
  gate: Yup.string(),
});

const CheckIn = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();

  const key = useMemo(
    () => location.search.slice(1).split("ID=")[1],
    [location.search]
  );

  const { data: appointment, isPending: isAppointmentLoading } =
    useGetCheckInDetailsById({ encryptedId: key });

  const { control, handleSubmit, setValue, watch } =
    useFormWithSchema(CheckInSchema);

  const gate = watch("gate");

  const user = useAuthStore((state) => state.user);

  const [isCheckInVisitorModalOpen, setIsCheckInVisitorModalOpen] =
    useState(false);

  const [isCheckOutVisitorModalOpen, setIsCheckOutVisitorModalOpen] =
    useState(false);

  const searchParams = new URLSearchParams(location.search);
  const type = searchParams.get("type");

  useEffect(() => {
    if (appointment && appointment.userGateID) {
      setValue("gate", appointment.userGateID);
    }
  }, [appointment, setValue]);

  const { data: allGates } = useGetAllGatesById({
    n_LocationID: appointment?.n_LocationID
      ? appointment?.n_LocationID.toString()
      : "",
    n_BuildingID: appointment?.n_BuildingID
      ? appointment?.n_BuildingID.toString()
      : "",
  });

  const gates = useMemo(
    () => mapToOptions("s_GateName_En", "s_GateName_Ar", "n_GateID", allGates),
    [allGates]
  );

  useEffect(() => {
    if (!key) {
      navigate(APP_ROUTES.HOME);
    } else {
      if (
        user?.n_RoleID !== USER_ROLES_IDS.RECEPTION &&
        user?.n_RoleID !== USER_ROLES_IDS.SECURITY_MANAGER
      ) {
        navigate(APP_ROUTES.HOME);
      }
    }
  }, [key, user, navigate]);

  const visitorPhoto = useMemo(
    () => appointment?.s_Photo && `data:image/png;base64,${appointment?.s_Photo}`,
    [appointment?.s_Photo]
  );

  const visitorInfo = useMemo(
    () => [
      {
        label: t("visitorName"),
        value: appointment?.s_VisitorName_En,
      },
      { label: t("companyName"), value: appointment?.s_Sponsor },
      { label: t("email"), value: appointment?.s_Email },
      {
        label: t("nationality"),
        value:
          i18n.language === "en"
            ? appointment?.s_Nationality_En
            : appointment?.s_Nationality_Ar,
      },
      { label: t("mobileNumber"), value: appointment?.s_MobileNo },
      {
        label: t("idType"),
        value:
          appointment?.n_DocumentType === DOC_TYPE_ID.NATIONAL_ID
            ? t("nationalID")
            : appointment?.n_DocumentType === DOC_TYPE_ID.PASSPORT
            ? t("passport")
            : appointment?.n_DocumentType === DOC_TYPE_ID.IQAMA
            ? t("iqama")
            : t("other"),
      },
      appointment?.n_DocumentType === DOC_TYPE_ID.OTHER
        ? {
            label: t("documentNameOther"),
            value: appointment?.s_OthersDoc,
          }
        : { label: "", value: "" },
      appointment?.n_DocumentType === DOC_TYPE_ID.NATIONAL_ID
        ? {
            label: t("nationalID"),
            value: appointment?.eidNumber,
          }
        : appointment?.n_DocumentType === DOC_TYPE_ID.PASSPORT
        ? {
            label: t("passportNumber"),
            value: appointment?.passportNumber,
          }
        : appointment?.n_DocumentType === DOC_TYPE_ID.IQAMA
        ? {
            label: t("iqamaNumber"),
            value: appointment?.s_Iqama,
          }
        : {
            label: t("documentNumberOther"),
            value: appointment?.s_OthersValue,
          },

      appointment?.n_DocumentType === DOC_TYPE_ID.NATIONAL_ID
        ? {
            label: t("nationalIDExpiryDate"),
            value:
              appointment?.dt_EIDExpiryDate &&
              formattedDate(new Date(appointment?.dt_EIDExpiryDate)),
          }
        : appointment?.n_DocumentType === DOC_TYPE_ID.PASSPORT
        ? {
            label: t("passportExpiryDate"),
            value:
              appointment?.dt_PassportExpiryDate &&
              formattedDate(new Date(appointment?.dt_PassportExpiryDate)),
          }
        : appointment?.n_DocumentType === DOC_TYPE_ID.IQAMA
        ? {
            label: t("iqamaExpiryDate"),
            value:
              appointment?.dt_IqamaExpiry &&
              formattedDate(new Date(appointment?.dt_IqamaExpiry)),
          }
        : {
            label: t("documentExpiryDateOther"),
            value:
              appointment?.dt_OthersExpiry &&
              formattedDate(new Date(appointment?.dt_OthersExpiry)),
          },
      {
        label: t("vehicleNo"),
        value:
          i18n.language === "en"
            ? appointment?.s_VehicleNo_En
            : appointment?.s_VehicleNo_Ar,
      },
    ],
    [
      t,
      appointment?.s_VisitorName_En,
      appointment?.s_Sponsor,
      appointment?.s_Email,
      appointment?.s_Nationality_En,
      appointment?.s_Nationality_Ar,
      appointment?.s_MobileNo,
      appointment?.n_DocumentType,
      appointment?.s_OthersDoc,
      appointment?.eidNumber,
      appointment?.passportNumber,
      appointment?.s_Iqama,
      appointment?.s_OthersValue,
      appointment?.dt_EIDExpiryDate,
      appointment?.dt_PassportExpiryDate,
      appointment?.dt_IqamaExpiry,
      appointment?.dt_OthersExpiry,
      appointment?.s_VehicleNo_En,
      appointment?.s_VehicleNo_Ar,
      i18n.language,
    ]
  );

  const visitDetails = useMemo(
    () => [
      {
        label: t("location"),
        value:
          i18n.language === "en"
            ? appointment?.s_LocationName_En
            : appointment?.s_LocationName_Ar,
      },
      {
        label: t("building"),
        value:
          i18n.language === "en"
            ? appointment?.s_BuildingName_En
            : appointment?.s_BuildingName_Ar,
      },
      {
        label: t("department"),
        value:
          i18n.language === "en"
            ? appointment?.s_DepartmentName_En
            : appointment?.s_DepartmentName_Ar,
      },
      {
        label: t("gate"),
        value:
          i18n.language === "en"
            ? appointment?.s_GateName_En
            : appointment?.s_GateName_Ar,
      },
      {
        label: t("meetingRoom"),
        value:
          i18n.language === "en"
            ? appointment?.s_MeetingRoomName_En
            : appointment?.s_MeetingRoomName_Ar,
      },
      {
        label: t("accessGroup"),
        value:
          i18n.language === "en"
            ? appointment?.s_AccessGroupName_En
            : appointment?.s_AccessGroupName_Ar,
      },
      {
        label: t("visitorType"),
        value:
          i18n.language === "en"
            ? appointment?.s_VisitType_En
            : appointment?.s_VisitType_Ar,
      },
      {
        label: t("purpose"),
        value:
          i18n.language === "en"
            ? appointment?.s_Purpose_En
            : appointment?.s_Purpose_Ar,
      },
      { label: t("hostName"), value: appointment?.s_HostName },
      {
        label: t("visitStartTime"),
        value: formattedDateTimeWithoutSec(
          appointment?.dt_AppointmentStartTime
        ),
      },
      {
        label: t("visitEndTime"),
        value: formattedDateTimeWithoutSec(appointment?.dt_AppointmentEndTime),
      },
      { label: t("note"), value: appointment?.s_HostComments },
      {
        label: t("allowedDevices"),
        value:
          i18n.language === "en"
            ? appointment?.s_AllowedDevicesDetails_En
            : appointment?.s_AllowedDevicesDetails_Ar,
      },
    ],
    [
      t,
      i18n.language,
      appointment?.s_LocationName_En,
      appointment?.s_LocationName_Ar,
      appointment?.s_BuildingName_En,
      appointment?.s_BuildingName_Ar,
      appointment?.s_DepartmentName_En,
      appointment?.s_DepartmentName_Ar,
      appointment?.s_GateName_En,
      appointment?.s_GateName_Ar,
      appointment?.s_MeetingRoomName_En,
      appointment?.s_MeetingRoomName_Ar,
      appointment?.s_AccessGroupName_En,
      appointment?.s_AccessGroupName_Ar,
      appointment?.s_VisitType_En,
      appointment?.s_VisitType_Ar,
      appointment?.s_Purpose_En,
      appointment?.s_Purpose_Ar,
      appointment?.s_HostName,
      appointment?.dt_AppointmentStartTime,
      appointment?.dt_AppointmentEndTime,
      appointment?.s_HostComments,
      appointment?.s_AllowedDevicesDetails_En,
      appointment?.s_AllowedDevicesDetails_Ar,
    ]
  );

  const onSubmit = useCallback(
    async (data: { gate?: string }) => {
      if (type === CHECK_IN_OUT_TYPE_KEYS.VEHICLE && !data.gate) {
        toast.error(
          location.pathname === APP_ROUTES.CHECK_IN
            ? t("kindlySelectCheckInGate")
            : t("kindlySelectCheckOutGate")
        );
        return;
      }

      if (
        user?.n_RoleID !== USER_ROLES_IDS.RECEPTION &&
        user?.n_RoleID !== USER_ROLES_IDS.SECURITY_MANAGER
      ) {
        toast.error(t("youAreNotAuthorisedToPerformThisAction"));
      } else if (appointment?.approvalStatus !== STATUS_IDS.APPROVED) {
        toast.error(t("visitIsNotApprovedYet"));
      } else {
        setIsCheckInVisitorModalOpen(true);
      }
    },
    [type, user?.n_RoleID, appointment?.approvalStatus, location.pathname, t]
  );

  const isLateCheckIn = useMemo(
    () => new Date(appointment?.dt_AppointmentEndTime) < new Date(),
    [appointment?.dt_AppointmentEndTime]
  );

  const [isPrintBadge, setIsPrintBadge] = useState<boolean>(false);
  const [isPrintBadgeWithoutPicture, setIsPrintBadgeWithoutPicture] =
    useState<boolean>(false);

  if (user?.n_RoleID === USER_ROLES_IDS.ADMIN)
    return (
      <div className="flex justify-center">
        <Loader />
      </div>
    );

  if (!isAppointmentLoading && !appointment) {
    return (
      <div className="flex flex-col gap-3">
        <p className="dark:text-white">{t("appointmentDoesNotExist")}</p>
        <Button
          type="button"
          onClick={() => navigate(APP_ROUTES.HOME)}
          className="bg-transparent ring-1 ring-purple-primary text-purple-primary hover:bg-transparent max-w-max dark:ring-white dark:text-white"
        >
          {t("goToDashboard")}
        </Button>
      </div>
    );
  }

  return (
    <>
      <Toaster position="top-right" reverseOrder={false} />

      <div className="flex flex-col gap-4 md:gap-8">
        <h1 className="text-xl md:text-2xl dark:text-white">
          {t("visitorDetails")}
        </h1>

        {isAppointmentLoading ? (
          <div className="flex justify-center">
            <Loader />
          </div>
        ) : (
          <form
            onSubmit={handleSubmit(onSubmit)}
            className="bg-white flex flex-col p-4 rounded-xl  shadow-[0px_1px_5px_0px_rgba(113,112,163,0.3)] "
          >
            {isLateCheckIn && (
              <div className="bg-red-500 max-w-max rounded-md px-1 mb-3">
                <p className="text-sm text-white">{t("visitTimeHasEnded")}</p>
              </div>
            )}

            <div className="flex flex-col md:flex-row md:border-b-[0.5px] border-gray-tertiary ">
              <div
                className={cn(
                  "md:w-[40%] flex flex-col gap-6 border-gray-tertiary pb-6 md:pb-7",
                  i18n.language === "en"
                    ? "md:border-r-[0.5px]"
                    : "md:border-l-[0.5px]"
                )}
              >
                {visitorPhoto && (
                  <div className="w-[120px] h-[120px] ring-2 ring-purple-primary/90 rounded-md overflow-hidden z-20 shadow-[-1px_3px_6px_0px_rgba(0,_0,_0,_0.1)]">
                    <img
                      src={visitorPhoto}
                      alt="visitor-photo"
                      className="w-full h-full object-contain"
                    />
                </div>
              )}
                <Section title={t("visitorInformation")}>
                  <InfoGrid
                    data={visitorInfo}
                    styles="grid-cols-1 sm:grid-cols-2"
                  />
                </Section>
              </div>

              <div
                className={cn(i18n.language === "en" ? "md:pl-4" : "md:pr-4")}
              >
                <Section title={t("visitDetails")}>
                  <InfoGrid
                    data={visitDetails.slice(0, -1)}
                    styles="grid-cols-1 sm:grid-cols-2 lg:grid-cols-3"
                  />
                  <InfoGrid
                    data={visitDetails.slice(-1)}
                    styles="grid-cols-1"
                  />
                </Section>
                {type === CHECK_IN_OUT_TYPE_KEYS.VEHICLE && (
                  <div className="mt-8 pb-2">
                    <Controller
                      name="gate"
                      control={control}
                      render={({ field: { onChange, value } }) => (
                        <ComboboxInput
                          label={
                            location.pathname === APP_ROUTES.CHECK_IN
                              ? t("checkInGate")
                              : t("checkOutGate")
                          }
                          labelStyle="text-purple-secondary text-base"
                          options={gates}
                          value={
                            gates?.find((item) => item.value === Number(value))
                              ?.labelEn || ""
                          }
                          onChange={onChange}
                        />
                      )}
                    />
                  </div>
                )}
              </div>
            </div>

            <div className="flex items-center justify-between pt-7 pb-5">
              {type !== CHECK_IN_OUT_TYPE_KEYS.VEHICLE &&
                location.pathname === APP_ROUTES.CHECK_IN &&
                appointment?.n_CheckedInStatus ===
                  VISITOR_VISIT_STATUSES.CHECKED_IN && (
                  <div className="bg-purple-primary text-white rounded-md px-2">
                    <p className="text-[15px]">
                      {t("visitorAlreadyCheckedIn")}
                    </p>
                  </div>
                )}

              {type !== CHECK_IN_OUT_TYPE_KEYS.VEHICLE &&
              location.pathname === APP_ROUTES.CHECK_OUT &&
              appointment?.n_CheckedInStatus !==
                VISITOR_VISIT_STATUSES.CHECKED_IN ? (
                <div className="bg-purple-primary text-white rounded-md px-2">
                  <p className="text-[15px]">
                    {t("visitorHasNotCheckedInYet")}
                  </p>
                </div>
              ) : (
                <div />
              )}

              <div className="flex items-center gap-2">
                {location.pathname === APP_ROUTES.CHECK_IN ? (
                  (type === CHECK_IN_OUT_TYPE_KEYS.VEHICLE ||
                    appointment?.n_CheckedInStatus !==
                      VISITOR_VISIT_STATUSES.CHECKED_IN) && (
                    <>
                      {type !== CHECK_IN_OUT_TYPE_KEYS.VEHICLE && (
                        <>
                          <CheckboxInput
                            key="print-badge"
                            id="print-badge"
                            label={t("printBadge")}
                            checked={isPrintBadge}
                            setChecked={() => setIsPrintBadge((prev) => !prev)}
                          />

                          {visitorPhoto && isPrintBadge && (
                            <CheckboxInput
                              key="print-badge-without-picture"
                              id="print-badge-without-picture"
                              label={t("printBadgeWithoutPicture")}
                              checked={isPrintBadgeWithoutPicture}
                              setChecked={() =>
                                setIsPrintBadgeWithoutPicture((prev) => !prev)
                              }
                            />
                          )}
                        </>
                      )}
                      <Button>{t("checkIn")}</Button>
                    </>
                  )
                ) : type === CHECK_IN_OUT_TYPE_KEYS.VEHICLE ||
                  appointment?.n_CheckedInStatus ===
                    VISITOR_VISIT_STATUSES.CHECKED_IN ? (
                  <Button
                    type="button"
                    onClick={() => setIsCheckOutVisitorModalOpen(true)}
                  >
                    {t("checkOut")}
                  </Button>
                ) : (
                  <div />
                )}
                <Button
                  type="button"
                  onClick={() => navigate(APP_ROUTES.HOME)}
                  className="bg-transparent ring-1 ring-purple-primary text-purple-primary hover:bg-transparent"
                >
                  {t("goToDashboard")}
                </Button>
              </div>
            </div>
          </form>
        )}
      </div>

      <CheckInVisitorModal
        appointment={appointment}
        isLateCheckIn={isLateCheckIn}
        isPrintBadge={isPrintBadge}
        isOpen={isCheckInVisitorModalOpen}
        onOpenChange={() => setIsCheckInVisitorModalOpen(false)}
        gate={gate}
        isPrintBadgeWithoutPicture={isPrintBadgeWithoutPicture}
      />

      <CheckOutVisitorModal
        appointmentId={appointment?.n_AppointmentID}
        visitEndDate={appointment?.dt_AppointmentEndTime}
        isOpen={isCheckOutVisitorModalOpen}
        onOpenChange={() => setIsCheckOutVisitorModalOpen(false)}
        gate={gate}
      />
    </>
  );
};

export default CheckIn;
